import Button from '@/Components/Button/Button';
import SearchFilter from '@/Components/SearchFilter/SearchFilter';
import LoadingRow from '@/Components/Table/LoadingRow';
import NoRecordsRow from '@/Components/Table/NoRecordsRow';
import Title from '@/Components/Title/Title';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import type LockModel from '@/Models/Lock/LockModel';
import LockType from '@/Models/LockType';
import CurrentLocksStore from '@/Stores/CurrentLocksStore';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel, getPaginationRowModel,
    getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, type MouseEventHandler } from 'react';
import * as API from '../../API';
import Styles from './../../Style/GenericTable.module.scss';
import TitleStyles from './../../Style/GenericTitle.module.scss';
import CurrentLocksStyle from './CurrentLocks.module.scss';
import { DobButton, DobTableWrapper } from '@dedalusdiit/dob-components';
import TableHeaderSortDirection from '@/Components/TableHeaderSortDirection/TableHeaderSortDirection';
import { t } from 'i18next';

interface CurrentLocksProps {
    initialState: FilingCabinetItem;
}

interface ImageCellProps {
    onClick: MouseEventHandler<HTMLButtonElement>;
}

const ImageCell = ({ onClick }: Readonly<ImageCellProps>): React.ReactElement => {
    return (
        <div className={CurrentLocksStyle.iconContainer}>
            <DobButton
                color="silent"
                className={CurrentLocksStyle.defaultIcon}
                icon="lock"
                onClick={onClick}
                title={t('Lock')}
            />
            <DobButton
                color="silent"
                className={CurrentLocksStyle.hoverIcon}
                icon="unlock"
                onClick={onClick}
                title={t('Unlock')}
            />
        </div>
    );
};

const CurrentLocks = observer(({ initialState }: CurrentLocksProps): React.ReactElement => {
    const fcContext = useFilingCabinetContext();

    const store = useMemo(() => new CurrentLocksStore(new FilingCabinetStore(initialState, fcContext)), [initialState]);

    const {
        pagination, sorting, globalFilter,
    } = store.state;

    const onUnlock = (ID: number) => {
        void API.locks.releaseLockByID(ID).then(() =>
            store.reload(),
        );
    };

    const deleteExpiredLocks = async (): Promise<void> => {
        void API.locks.checkAndDeleteExpiredLocks().then(() => store.reload());
    };

    const columnHelper = useMemo(() => createColumnHelper<LockModel>(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('Id', {
            header: t('#'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('LockType', {
            header: t('Type'),
            cell: (info) => Object.keys(LockType).find((key) =>
                LockType[key as keyof typeof LockType] === Number.parseInt(info.getValue())),
        }),
        columnHelper.accessor('RecordId', {
            header: t('RecordID'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('PatientName', {
            header: t('PatientName'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('LockedBy', {
            header: t('LockedBy'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('TimeStamp', {
            header: t('TimeStamp'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('Duration', {
            header: t('Duration') + ' (m)',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('HostName', {
            header: t('HostName'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('IpAddress', {
            header: t('IPAddress'),
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('UnLock', {
            header: t('Unlock'),
            cell: (info) => <ImageCell onClick={() => onUnlock(info.row.original.Id)} />,
        }),
    ], [onUnlock]);

    const table = useReactTable({
        columns,
        data: store.filteredLocks,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: (updater) => {
            const newVal = updater instanceof Function ? updater(pagination) : updater;
            store.setPagination(newVal);
        },
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: (updater) => {
            const newVal = updater instanceof Function ? updater(sorting) : updater;
            store.setSorting(newVal);
        },
        state: {
            sorting,
            pagination,
        },
    });

    const onSetGlobalFilter = useCallback((filter: string): void => {
        store.set('globalFilter', filter);
    }, [store]);

    return (
        <>
            <Title titleName={t('currentLocks')}>
                <div className={TitleStyles.buttons}>
                    <Button
                        mediumGreen={true}
                        onClick={() => {
                            void deleteExpiredLocks();
                        }}
                        title={t('DeleteExpired')}
                        silent={true}
                        onDark={true}
                    >
                        {t('DeleteExpired')}
                    </Button>
                </div>
            </Title>

            {/* Filters */}
            <div className={Styles.tableOverviewFilters}>
                <SearchFilter
                    initialValue={globalFilter}
                    onSetGlobalFilter={onSetGlobalFilter}
                    placeholder={t('Search') + ' (' + t('PatientName')
                        + ', ' + t('LockedBy') + ', ' + t('HostName') + ')'}
                />
            </div>

            {/* Table */}
            <div className={Styles.tabledataOverview}>
                <DobTableWrapper color="primary">
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} {...{ onClick: header.column.getToggleSortingHandler() }}>
                                            <div>
                                                <span>{flexRender(t(header.column.columnDef.header as string), header.getContext())}</span>
                                                <TableHeaderSortDirection sortDirection={header.column.getIsSorted()} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {store.loading && <LoadingRow colSpan={columns.length} />}
                            {!store.loading && table.getRowModel().rows.length === 0 && <NoRecordsRow colSpan={columns.length} />}
                            {table.getRowModel().rows.map((row) => (
                                <tr
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            style={{
                                                width: cell.column.getSize(),
                                            }}
                                            key={cell.id}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DobTableWrapper>
                {/* Footer */}
                <div className={Styles.tableFooter}>
                    <div className={Styles.footerActions}>
                        <span>
                            {(table.getState().pagination.pageIndex * table.getState().pagination.pageSize) + 1}
                            {' '}
                            -
                            {' '}
                            {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getRowCount())}
                            {' '}
                            {t('of')}
                            {' '}
                            {table.getRowCount()}
                        </span>
                        <Button silent smallPadding mediumGreen onDark onClick={store.reload}>
                            <div>
                                <span className="material-symbols-outlined ">refresh</span>
                                {' '}
                                <span>{t('Reload')}</span>
                            </div>
                        </Button>
                    </div>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                        {[10, 25, 50, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>
                    <div className={Styles.pagination}>
                        <Button onDark small silent onClick={() => table.firstPage()}>
                            <span className="material-symbols-outlined ">keyboard_double_arrow_left</span>
                        </Button>
                        <Button onDark small silent onClick={() => table.previousPage()}>
                            <span className="material-symbols-outlined ">keyboard_arrow_left</span>
                        </Button>
                        <div>{table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</div>
                        <Button onDark small silent onClick={() => table.getCanNextPage() ? table.nextPage() : null}>
                            <span className="material-symbols-outlined ">keyboard_arrow_right</span>
                        </Button>
                        <Button onDark small silent onClick={() => table.getCanNextPage() ? table.lastPage() : null}>
                            <span className="material-symbols-outlined ">keyboard_double_arrow_Right</span>
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
});

export default CurrentLocks;
